import { serverUrl, getDefaultHeaders } from 'services/authService';
import { Connector } from 'models/Connector';

const fetchConnectorsForConnectorGroup = async (
  connectorGroupId: number,
  accessToken: string
): Promise<Connector[]> => {
  const response = await fetch(
    `${serverUrl}/connector_group/${connectorGroupId}/connectors`,
    {
      headers: getDefaultHeaders(accessToken),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch connectors for the connector group');
  }

  return response.json();
};

const getConnector = async (id: number, accessToken: string): Promise<Connector> => {
  const response = await fetch(`${serverUrl}/connector/${id}`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connector');
  }

  return response.json();
};

const createConnector = async (
  connectorData: Omit<Connector, 'id'>,
  accessToken: string
): Promise<Connector> => {
  try {
    const response = await fetch(`${serverUrl}/connector`, {
      method: 'POST',
      headers: getDefaultHeaders(accessToken),
      body: JSON.stringify(connectorData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error creating connector:', errorData);
      throw new Error(errorData.message || 'Failed to create connector');
    }

    return response.json();
  } catch (error) {
    console.error('Error in createConnector:', error);
    throw error;
  }
};

const updateConnector = async (
  id: number,
  connectorData: Connector,
  accessToken: string
): Promise<Connector> => {
  try {
    // Backend does not allow updating username and rejects the request if it is present.
    // Send only fields that ConnectorEditForm accepts: password, api_key, endpoint, remote_directory, enabled, carrier.
    const {
      username: _u,
      id: _id,
      status: _status,
      status_code: _sc,
      status_message: _sm,
      statusMessage: _sm2,
      connector_group_id: _cgi,
      ...updatePayload
    } = connectorData;
    const body = {
      password: updatePayload.password,
      api_key: updatePayload.api_key ?? '',
      endpoint: updatePayload.endpoint ?? '',
      remote_directory: updatePayload.remote_directory ?? '',
      enabled: updatePayload.enabled,
      carrier: updatePayload.carrier,
    };

    const response = await fetch(`${serverUrl}/connector/${id}`, {
      method: 'PUT',
      headers: getDefaultHeaders(accessToken),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to update connector';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message ?? errorMessage;
        console.error('Error updating connector:', errorData);
      } catch {
        console.error('Error updating connector: non-JSON response', response.status);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    console.error('Error in updateConnector:', error);
    throw error;
  }
};

const deleteConnector = async (
  id: number,
  accessToken: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${serverUrl}/connector/${id}`, {
      method: 'DELETE',
      headers: getDefaultHeaders(accessToken),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to delete connector';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message ?? errorMessage;
      } catch {
        console.error('Error deleting connector: non-JSON response', response.status);
      }
      throw new Error(errorMessage);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : { message: 'Connector deleted!' };
  } catch (error) {
    console.error('Error in deleteConnector:', error);
    throw error;
  }
};

export {
  fetchConnectorsForConnectorGroup,
  getConnector,
  createConnector,
  updateConnector,
  deleteConnector,
};
